/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { Loading, VerticalStepper } from "../../../components";
import { StyledBookingFlowButton } from "../BookingScheduleContainer/BookingScheduleContainer.style";
import EmailInputWithChips from "../../../components/CustomerReferrals/EmailInputWithChips";
import InputWithCopy from "../../../components/CustomerReferrals/InputWithCopy";
import CircularProgress from "@mui/material/CircularProgress";
import TaskAlt from "@mui/icons-material/TaskAlt";
import { useCustomerReferral } from "../../../services/customer-referral/customer-referral.service";
import { useDispatch } from "react-redux";
import { toastMessageActions } from "../../../state/request";
import GvSecondaryButton from "../../../components/GvSecondaryButton/GvSecondaryButton";
import { ShareViaSms } from "../../../components/Icons";
import { useMediaQuery, useTheme } from "@mui/material";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { REFERRAL_ADVOCATE_AMOUNT, REFERRAL_REFEREE_AMOUNT } from "../../../config/params";
import { userActivityLogger } from "../../../utils/mixpanel";
import { useSearchParams } from "react-router-dom";

const ReferAFriendContainer = () => {
  const stepData = [
    {
      label: "Share your link",
      description: "Send your personal referral link to friends via text, email, or social — whatever's easiest.",
    },
    {
      label: "Your friend books",
      description: `When your friend books their first visit at GoodVets using your link,
       they'll get ${REFERRAL_REFEREE_AMOUNT} off their appointment.`,
    },
    {
      label: `You get ${REFERRAL_ADVOCATE_AMOUNT}!`,
      description: `Once their visit is complete, you'll receive a ${REFERRAL_ADVOCATE_AMOUNT} credit
       on your GoodVets account to use for future visits.`,
    },
  ];

  const { getReferralUrl, referralUrl, sendInvite, inviteLoading, inviteData, referralCode } = useCustomerReferral();
  const [emails, setEmails] = useState<string[]>([]);
  const [emailSent, setEmailSent] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between(700, 1100));
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("source") === "left_nav") {
      userActivityLogger(MIXPANEL_EVENTS.
        USER_CLICK_REFER_A_FRIEND, {
        source: "left_nav",
        user_session_type: "logged_in",
      });
    }
    getReferralUrl();
  }, []);

  useEffect(() => {
    if (inviteData?.success) {
      userActivityLogger(MIXPANEL_EVENTS.SEND_EMAIL_INVITE_SUCCESS, {
        referral_code: referralCode,
        friend_emails: emails.join(","),
        no_of_emails: emails.length,
        user_session_type: "logged_in",
      });
      setEmailSent(true);
      setEmails([]);
      setTimeout(() => {
        setEmailSent(false);
      }, 4000);
    } else if (inviteData?.success === false) {
      userActivityLogger(MIXPANEL_EVENTS.SEND_EMAIL_INVITE_FAILURE, {
        referral_code: referralCode,
        friend_emails: emails.join(","),
        no_of_emails: emails.length,
        user_session_type: "logged_in",
        error_message: inviteData?.messages[0],
      });
      setEmails([]);
      dispatch(toastMessageActions.setGlobalAlert({ message: inviteData?.messages[0] || "Error Sending Invite",
        severity: "error" }));
    }
  }, [inviteData]);

  const handleSendEmails = () => {
    sendInvite(emails);
    userActivityLogger(MIXPANEL_EVENTS.SEND_EMAIL_INVITE_TRIGGERED, {
      friend_emails: emails.join(","),
      no_of_emails: emails.length,
      user_session_type: "logged_in",
    });
  };

  const handleShareViaSms = () => {
    // eslint-disable-next-line max-len
    window.open(`sms:?body=We’ve had such a great experience with GoodVets. If you’re looking for a vet, use my link for ${REFERRAL_REFEREE_AMOUNT} off your first visit. 🐾 :%20${referralUrl}`, "_self");
    userActivityLogger(MIXPANEL_EVENTS.SHARE_VIA_SMS_CLICKED, {});
  };

  return (
    <div className="space-y-2">
      <div>
        <img src="/assets/icons/refer-friends-dog-pic.png" className="w-full max-h-96 object-cover rounded-lg"
          alt="image of dog holding ball" />
      </div>
      <div className="flex flex-wrap md:flex-nowrap justify-between gap-4">
        <div className="border border-taupe-50 rounded-md p-6 w-full md:w-8/12 bg-white space-y-4">
          <div className="space-y-2">
            <h3 className="text-2xl grenette-pro text-center">Invite Friends and Get {REFERRAL_ADVOCATE_AMOUNT}!</h3>
            <p className="text-sm text-center px-6">
            Share the care and get rewarded!
            Invite friends to GoodVets and you&apos;ll earn {REFERRAL_ADVOCATE_AMOUNT} in credits when they
            complete their first visit.
            </p>
          </div>
          <div>
            <InputWithCopy value={referralUrl || ""} label="Copy Referral Link" referralCode={referralCode || ""} />
            <div className="flex items-center w-full my-2">
              <div className="flex-grow border-t-2 border-taupe-15"></div>
              <span className="mx-1 text-sm bg-white px-2">or</span>
              <div className="flex-grow border-t-2 border-taupe-15"></div>
            </div>

            {/* Invite via Email */}
            {emailSent?
              <div>
                <label className="text-sm font-semibold block mb-1">Invite via Email</label>
                <div className="flex flex-col justify-center items-center gap-4">
                  <TaskAlt sx={{ fontSize: 45 }} color="success" />
                  <span className="text-sm font-semibold">Email invite sent!</span>
                </div>
              </div>:
              <>
                <EmailInputWithChips label="Invite via Email" emails={emails} setEmails={setEmails} />
                <StyledBookingFlowButton
                  disabled={emails.length === 0}
                  fullWidth
                  color="primary"
                  className="!text-sm"
                  size="small"
                  onClick={handleSendEmails}
                >
                  {inviteLoading ? <CircularProgress color="inherit" size={20} /> : "Send Email Invite"}
                </StyledBookingFlowButton></>}
            {/* or */}
            { (isMobile || isTablet) &&
            <>
              <div className="flex items-center w-full my-2">
                <div className="flex-grow border-t-2 border-taupe-15"></div>
                <span className="mx-1 text-sm bg-white px-2">or</span>
                <div className="flex-grow border-t-2 border-taupe-15"></div>
              </div>
              {/* Share via SMS */}
              <GvSecondaryButton
                type="button"
                onClick={handleShareViaSms}
                className="!w-full !font-semibold !border-1 !border-gray-4 hover:!bg-gray-3 !h-[2.5rem] !text-sm"
              >
                <span><ShareViaSms /></span>&nbsp;
               Share via SMS
              </GvSecondaryButton>
            </>}

            <p className="text-xs text-center py-2">By referring a friend, I accept the&nbsp;
              <a href="https://goodvets.com/terms"
                target="_blank" className="underline" rel="noreferrer">Terms & Conditions</a>
            </p>
          </div>
        </div>
        <div className="border border-taupe-50 rounded-md bg-taupe-15 p-6 w-full md:w-4/12 h-fit">
          <h3 className="font-bold">How it Works</h3>
          <div>
            <VerticalStepper steps={stepData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferAFriendContainer;
